import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [validSession, setValidSession] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Supabase puts the access token in the URL hash after the reset link is clicked
    // It auto-exchanges the token and sets a session
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setValidSession(true);
      }
      setChecking(false);
    });

    // Also check immediately in case session is already set
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setValidSession(true);
        setChecking(false);
      }
    });
  }, []);

  async function handleReset(e) {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setError(error.message);
    } else {
      setDone(true);
      // Sign out and redirect to login after 2 seconds
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/';
      }, 2000);
    }
    setLoading(false);
  }

  const cardStyle = {
    position: 'fixed', inset: 0,
    background: 'linear-gradient(135deg, #152B3E 0%, #1a3850 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'Poppins, sans-serif'
  };

  const innerStyle = {
    background: '#fff', borderRadius: 18, padding: '44px 40px',
    width: 400, maxWidth: '95vw',
    boxShadow: '0 20px 56px rgba(0,0,0,0.28)'
  };

  if (checking) {
    return (
      <div style={cardStyle}>
        <div style={innerStyle}>
          <div style={{ textAlign: 'center', color: '#555', fontSize: 14 }}>Verifying your reset link...</div>
        </div>
      </div>
    );
  }

  if (!validSession) {
    return (
      <div style={cardStyle}>
        <div style={innerStyle}>
          <div style={{ textAlign: 'center', marginBottom: 16, fontSize: 36 }}>⚠️</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#152B3E', marginBottom: 8, textAlign: 'center' }}>Link expired or invalid</div>
          <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, textAlign: 'center', marginBottom: 24 }}>
            This password reset link has expired or already been used. Reset links are valid for 1 hour.
          </div>
          <a href="/" style={{
            display: 'block', textAlign: 'center', background: '#152B3E', color: '#fff',
            padding: '12px 0', borderRadius: 8, textDecoration: 'none',
            fontWeight: 600, fontSize: 14
          }}>Back to Sign In</a>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={cardStyle}>
        <div style={innerStyle}>
          <div style={{ textAlign: 'center', marginBottom: 16, fontSize: 36 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#152B3E', marginBottom: 8, textAlign: 'center' }}>Password updated!</div>
          <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, textAlign: 'center' }}>
            Your password has been reset successfully. Redirecting you to sign in...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={innerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#1C90A4', fontWeight: 700, marginBottom: 4 }}>Stimulyst</div>
          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 26, fontWeight: 700, color: '#152B3E' }}>doer-seller</div>
          <div style={{ fontSize: 13, color: '#999', marginTop: 4 }}>Set your new password</div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(200,16,46,0.06)', border: '1px solid rgba(200,16,46,0.2)',
            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
            fontSize: 13, color: '#C8102E', lineHeight: 1.5
          }}>{error}</div>
        )}

        <form onSubmit={handleReset}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#152B3E', marginBottom: 4 }}>New Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              autoFocus
              style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #DDD9D0', borderRadius: 7, fontFamily: 'Poppins, sans-serif', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#152B3E', marginBottom: 4 }}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #DDD9D0', borderRadius: 7, fontFamily: 'Poppins, sans-serif', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px 0', background: '#152B3E', color: '#fff',
              border: 'none', borderRadius: 8, fontFamily: 'Poppins, sans-serif',
              fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Updating...' : 'Set New Password'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: '#999' }}>
          Powered by Stimulyst © 2026
        </div>
      </div>
    </div>
  );
}
